/**
 * AuditAI Module System Types
 * 
 * Defines the core types for the modular audit system with 8 modules (0-7).
 */

// ============= Module Status =============

export type ModuleStatus = 'not_started' | 'running' | 'completed';

export type DataGateStatus = 'not_run' | 'passed' | 'passed_with_warnings' | 'failed';

export type FindingStatus = 'open' | 'in_progress' | 'resolved' | 'dismissed';

export type FindingSeverity = 'low' | 'medium' | 'high' | 'critical';

// ============= File Key Enum =============

export type FileKey = 
  | 'GL_SIE'           // General Ledger SIE file (current year)
  | 'GL_SIE_PREV'      // General Ledger SIE file (previous year)
  | 'COA'              // Chart of Accounts
  | 'JOURNAL_META'     // Journal metadata
  | 'AP_OPEN_ITEMS'    // Accounts Payable open items
  | 'AR_OPEN_ITEMS'    // Accounts Receivable open items
  | 'BANK_TX'          // Bank transactions
  | 'TAX_ACCOUNT'      // Swedish Tax Account (Skattekonto)
  | 'VAT_REPORT'       // VAT report
  | 'VAT_TX_DETAIL'    // VAT transaction details
  | 'VENDOR_MASTER'    // Vendor master data
  | 'CUSTOMER_MASTER'  // Customer master data
  | 'AGI_REPORTS';     // AGI reports

// ============= Module Tab Definition =============

export type ModuleTabId = 'overview' | 'files' | 'run' | 'results' | 'actions';

export interface ModuleTab {
  id: ModuleTabId;
  nameKey: string; // i18n key
  order: number;
}

// ============= Standard Module Tabs =============

export const STANDARD_MODULE_TABS: ModuleTab[] = [
  { id: 'overview', nameKey: 'modules.tabs.overview', order: 1 },
  { id: 'files', nameKey: 'modules.tabs.files', order: 2 },
  { id: 'run', nameKey: 'modules.tabs.run', order: 3 },
  { id: 'results', nameKey: 'modules.tabs.results', order: 4 },
  { id: 'actions', nameKey: 'modules.tabs.actions', order: 5 },
];

// ============= Module Definition =============

/** AI provider preference for module routing */
export type AIProvider = 'azure' | 'google' | 'auto';

/**
 * Semantic module IDs - these are stable and never change.
 * GUI ordering is controlled by displayOrder, not by ID.
 */
export type SemanticModuleId = 
  | 'data_quality'           // Datakvalitet
  | 'sie_accounting_basis'   // Redovisningsgrund (SIE)
  | 'journal_entries'        // Manuella verifikationer
  | 'accounts_payable'       // Leverantörsskulder
  | 'accounts_receivable'    // Kundfordringar
  | 'bank_cash'              // Bank och kassa
  | 'taxes_fees'             // Skattekonto
  | 'vat'                    // Moms
  | 'duplicates_anomalies';  // Dubletter och avvikelser

export interface ModuleDefinition {
  /** Semantic ID - stable, never changes (e.g., 'taxes_fees') */
  id: SemanticModuleId;
  /** Display order in GUI (0-8). Can be changed without affecting data. */
  displayOrder: number;
  /** Legacy IDs for backward compatibility with existing database data */
  legacyIds: string[];
  name: string;                  // Swedish name
  nameKey: string;               // i18n key for name
  shortDescription: string;      // Short description
  description: string;           // Full "Om" description for Overview tab
  internalDescription: string;   // Technical description
  requiredFiles: FileKey[];      // Required file types
  recommendedFiles?: FileKey[];  // Recommended file types (shown with warning if missing)
  optionalFiles: FileKey[];      // Optional file types
  tabs: ModuleTab[];             // Standard tabs
  edgeFunctionName?: string;     // Edge function to call (if any)
  isDataGate?: boolean;          // True for Module 0
  isSieModule?: boolean;         // True for SIE/Accounting Basis module
  
  // Analysis configuration
  /** Default tolerance days for date-based analyses */
  defaultTolerance?: number;
  /** Supported file types for upload (e.g., ['csv', 'txt']) */
  supportedFileTypes?: string[];
  /** Whether this module requires field mapping before analysis */
  requiresFieldMapping?: boolean;
  /** Preferred AI provider for this module. 'auto' uses routing based on token size. */
  preferredProvider?: AIProvider;
  /** Maximum expected tokens for this module. Used for routing decisions. */
  maxExpectedTokens?: number;
}

// ============= Module State (Runtime) =============

export interface ModuleState {
  moduleId: string;
  status: ModuleStatus;
  selected: boolean;
  filesProgress: {
    required: number;
    requiredUploaded: number;
    optional: number;
    optionalUploaded: number;
  };
  lastRunId?: string;
  lastRunAt?: string;
  findingsCount?: number;
  
  // Data Quality Metrics (aggregated from batches)
  /** Quality score 0.0-1.0 for data used in this module. */
  dataQualityScore?: number;
  /** List of data quality issues found during processing. */
  dataQualityIssues?: string[];
}

// ============= Engagement File =============

export interface EngagementFile {
  id: string;
  fileKey: FileKey;
  fileName: string;
  originalFileName: string;
  uploadedAt: string;
  uploadedBy: string;
  sourceModuleId?: string;       // Which module first uploaded it
  parsedHeaders?: string[];      // Detected CSV/Excel headers
  mapping?: Record<string, string>; // Field mapping
  status: 'uploaded' | 'parsed' | 'mapped' | 'error';
  storagePath: string;
  mimeType: string;
  sizeBytes: number;
}

// ============= Finding (Extended) =============

export interface AuditFindingExtended {
  id: string;
  auditId: string;
  moduleId: string;
  runId?: string;
  severity: FindingSeverity;
  title: string;
  findingType: string;
  description: string;
  whyItMatters?: string;
  sourceFiles?: string[];
  evidencePointer?: string;
  recommendedNextStep?: string;
  recommendedEvidence?: string;
  status: FindingStatus;
  flaggedByModel: string;
  dataReference?: Record<string, unknown>;
  createdAt: string;
  updatedAt?: string;
  
  // AI Quality Metadata
  /** AI/rule confidence score 0.0-1.0. Higher = more certain. */
  aiConfidence?: number;
  /** True if finding lacks sufficient evidence and needs verification. */
  isHypothesis?: boolean;
  /** Version of the prompt/rules that generated this finding (e.g., 5.0.0-hybrid). */
  promptVersion?: string;
  
  // Audit Workflow Metadata
  /** True if finding is ready for inclusion in audit report. */
  auditReady?: boolean;
  /** True if finding needs senior/partner review before resolution. */
  requiresPartnerReview?: boolean;
  /** Minimum user role required to view this finding. */
  minRoleToView?: string;
  /** Minimum user role required to change status of this finding. */
  minRoleToResolve?: string;
  
  // Data Quality Context
  /** Quality score 0.0-1.0 of the underlying data used for this finding. */
  dataQualityScore?: number;
  /** Array of data quality issues affecting this finding. */
  dataQualityIssues?: string[];
  
  // Cross-module Relations
  /** UUIDs of related findings across modules. */
  relatedFindingIds?: string[];
  /** Module IDs that must be run before this finding is valid. */
  dependsOnModules?: string[];
}

// ============= Action Item =============

export interface ActionItem {
  id: string;
  findingId: string;
  actionText: string;
  status: 'pending' | 'in_progress' | 'completed';
  owner?: string;
  dueDate?: string;
  createdAt: string;
  updatedAt?: string;
}

// ============= Materiality Thresholds =============

export type MaterialityBasis = 
  | 'result_before_tax' 
  | 'revenue' 
  | 'equity' 
  | 'total_assets'
  | 'custom';

export type SpecificMaterialityArea = 
  | 'related_parties' 
  | 'management_compensation' 
  | 'legal_compliance'
  | 'regulatory_matters'
  | 'fraud_risk';

export interface MaterialityThreshold {
  value: number;                        // Belopp i SEK
  basis?: MaterialityBasis;             // T.ex. 'result_before_tax', 'revenue'
  percentage?: number;                  // Procent av bas (för spårbarhet)
  derived_from?: 'overall_materiality'; // Varifrån värdet härleds
  qualitative?: boolean;                // Sant för specifik väsentlighet
  applies_to?: SpecificMaterialityArea[]; // Vilka områden den gäller
}

export interface MaterialityThresholds {
  overall_materiality?: MaterialityThreshold;
  performance_materiality?: MaterialityThreshold;
  specific_materiality?: MaterialityThreshold;
  clearly_trivial_threshold?: MaterialityThreshold;
}

// ============= Audit Engagement Config =============

export interface AuditEngagementConfig {
  // Företagskonfiguration
  companySize?: 'small' | 'large';
  vatReportingPeriod?: 'monthly' | 'quarterly';
  toleranceDays?: number;
  
  // Revisionströsklar
  thresholds?: MaterialityThresholds;
}

// ============= Helper Types =============

export interface FileRequirement {
  fileKey: FileKey;
  required: boolean;
  nameKey: string;
  descriptionKey: string;
  formats: string[];
}

export interface ModuleFilesConfig {
  moduleId: string;
  requirements: FileRequirement[];
}
